import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CheckCircle, X, PaperPlaneTilt, WhatsappLogo, EnvelopeSimple } from "@phosphor-icons/react";

export default function PendingApprovals() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState({});

  const load = async () => {
    try { const { data } = await api.get("/notifications/pending-approvals"); setItems(data); }
    catch {}
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  const approve = async (b) => {
    setBusy({ ...busy, [b.id]: "approve" });
    try {
      const { data } = await api.post(`/notifications/approve/${b.id}`);
      if (data.queued?.length) {
        toast.success(`Notified via ${data.queued.join(" + ")} · ${b.batch_no}`);
      } else {
        toast.info("Approved · no channels configured (visit Settings to enable WhatsApp / Email)", { duration: 5000 });
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy({ ...busy, [b.id]: null }); }
  };

  const dismiss = async (b) => {
    setBusy({ ...busy, [b.id]: "dismiss" });
    try {
      await api.post(`/notifications/dismiss/${b.id}`);
      toast.success(`Dismissed ${b.batch_no}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy({ ...busy, [b.id]: null }); }
  };

  if (items.length === 0) return null;

  return (
    <Card className="shadow-none mb-6 border-primary/40" data-testid="pending-approvals-card">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline flex items-center gap-2">
              <PaperPlaneTilt size={13} weight="fill" className="text-primary"/>
              Awaiting External Notification
            </div>
            <div className="font-display font-bold text-lg tracking-tight">Approve & Notify · {items.length} completed batch{items.length > 1 ? "es" : ""}</div>
          </div>
          <Badge className="bg-primary/10 text-primary hover:bg-primary/10 mono">{items.length}</Badge>
        </div>

        <div className="space-y-3">
          {items.map(b => (
            <div key={b.id} className="border border-border rounded-md p-4 bg-card" data-testid={`approval-row-${b.batch_no}`}>
              <div className="flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-2 mb-1">
                    <div className="mono font-black text-sm">{b.batch_no}</div>
                    <div className="font-semibold text-sm truncate">{b.sku_name}</div>
                    <Badge variant="secondary" className="text-[10px]">{b.sku_code}</Badge>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-[11px] mt-2">
                    <div><div className="overline">Packed</div><div className="mono font-semibold">{b.packed_qty || 0} pcs</div></div>
                    <div><div className="overline">Yield</div><div className="mono font-semibold text-emerald-600">{b.yield_pct || 0}%</div></div>
                    <div><div className="overline">Moisture</div><div className="mono font-semibold">{b.moisture_pct || 0}%</div></div>
                    <div><div className="overline">Machine</div><div className="mono text-xs truncate">{b.machine_name || "—"}</div></div>
                    <div><div className="overline">QC</div><div>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${b.qc_status === "Passed" ? "bg-emerald-100 text-emerald-700" : b.qc_status === "Failed" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"}`}>{b.qc_status || "Pending"}</span>
                    </div></div>
                  </div>
                </div>
                <div className="flex items-center gap-2 md:border-l md:border-border md:pl-4">
                  <Button variant="outline" size="sm" onClick={()=>dismiss(b)} disabled={busy[b.id]} data-testid={`dismiss-${b.batch_no}`}>
                    <X size={14} className="mr-1"/> Dismiss
                  </Button>
                  <Button size="sm" onClick={()=>approve(b)} disabled={busy[b.id]} data-testid={`approve-${b.batch_no}`}>
                    <CheckCircle size={14} className="mr-1"/>
                    {busy[b.id] === "approve" ? "Sending…" : "Approve & Notify"}
                  </Button>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-border/60 flex items-center gap-3 text-[10px] text-muted-foreground">
                <span className="inline-flex items-center gap-1"><WhatsappLogo size={12} weight="fill" className="text-emerald-600"/> WhatsApp</span>
                <span className="inline-flex items-center gap-1"><EnvelopeSimple size={12} weight="fill" className="text-blue-600"/> Email</span>
                <span>· will be delivered via the channels enabled in Settings</span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
